import { useTimeZone } from '@/auth/useTimeZone.js';
import { FormModal, SubmitProps } from '@/components/form-modal.js';
import { DropdownMenuItem } from '@/components/ui/dropdown-menu.js';
import { useToast } from '@/components/ui/hooks/use-toast.js';
import { emptySelectOption } from '@/components/ui/multi-select-utils.js';
import {
  getPendingStudentGroupReassignmentRequests,
  getStudentGroup,
  getStudentGroupReassignmentRequests,
  getStudentGroups,
  updateStudentGroupReassignmentRequest,
} from '@/lib/api-calls.js';
import { airServerErrorFrom } from '@/lib/error-handling.js';
import {
  getReassignmentRequestFormDefaultValues,
  ReassignmentRequestFormValues,
  reassignmentRequestFormSchema,
} from '@/lib/student-group-reassignment-utils.js';
import { ReassignmentRequestForm } from '@/routes/_layout/student-groups/-components/reassignment-request-form.js';
import {
  PendingStudentGroupReassignmentRequest,
  StudentGroupReassignmentRequestStatus,
  studentGroupReassignmentReasonOptions,
  studentGroupReassignmentReasonSchema,
} from '@airreading/common/data';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ReactNode, useMemo } from 'react';

export interface BulkEditReassignmentRequestProps {
  reassignmentRequests: PendingStudentGroupReassignmentRequest[];
  onSuccess?: () => void;
  trigger?: ReactNode;
}

export const BulkEditReassignmentRequest = ({
  reassignmentRequests,
  onSuccess,
  trigger,
}: BulkEditReassignmentRequestProps) => {
  const { toast } = useToast();
  const { timeZone } = useTimeZone();
  const queryClient = useQueryClient();
  const selectedReassignmentRequest = reassignmentRequests.length === 1 ? reassignmentRequests[0] : undefined;
  const isAvailable =
    !!selectedReassignmentRequest &&
    selectedReassignmentRequest.displayStatus === StudentGroupReassignmentRequestStatus.requested;

  const { mutateAsync } = useMutation({
    mutationFn: updateStudentGroupReassignmentRequest,
  });

  const countAsReassignedLabel = selectedReassignmentRequest?.originalCoach
    ? selectedReassignmentRequest.originalCoach.fullNameOrEmail
      ? `Count as a reassigned student group from ${selectedReassignmentRequest.originalCoach.fullNameOrEmail}`
      : 'Count as a reassigned student group from the previous teacher'
    : null;

  const defaultValues = useMemo(
    () =>
      getReassignmentRequestFormDefaultValues(
        timeZone,
        selectedReassignmentRequest
          ? {
              effectiveDate: selectedReassignmentRequest.effectivePlainDate,
              requester: {
                label: selectedReassignmentRequest.requester.fullNameOrEmail ?? '',
                value: selectedReassignmentRequest.requester.id,
              },
              reassignmentReasons: selectedReassignmentRequest.reasons.map((reason) => {
                const option = studentGroupReassignmentReasonOptions.find(({ value }) => value === reason);
                return option ?? { label: reason, value: reason };
              }),
              preferredNewTeacher: selectedReassignmentRequest.preferredCoach
                ? {
                    label: selectedReassignmentRequest.preferredCoach.fullNameOrEmail ?? '',
                    value: selectedReassignmentRequest.preferredCoach.id,
                  }
                : emptySelectOption,
              specialRequirements: selectedReassignmentRequest.specialRequirements ?? '',
              countAsReassignedFromTestTeacher: selectedReassignmentRequest.countAsReassignedFromTestTeacher,
            }
          : undefined,
      ),
    [selectedReassignmentRequest, timeZone],
  );

  const onSubmit = async ({ formData, closeModal, reset }: SubmitProps<ReassignmentRequestFormValues>) => {
    if (!selectedReassignmentRequest) {
      return;
    }

    const preferredCoachId = formData.preferredNewTeacher.value.trim() || null;
    const specialRequirements = formData.specialRequirements?.trim() || null;

    try {
      await mutateAsync({
        reassignmentRequestId: selectedReassignmentRequest.id,
        data: {
          effectivePlainDate: formData.effectiveDate,
          requesterId: formData.requester.value,
          reasons: formData.reassignmentReasons.map(({ value }) => studentGroupReassignmentReasonSchema.parse(value)),
          preferredCoachId,
          specialRequirements,
          countAsReassignedFromTestTeacher: formData.countAsReassignedFromTestTeacher,
        },
      });

      toast({
        title: 'Success',
        description: 'Reassignment request updated successfully',
      });

      queryClient.invalidateQueries(getStudentGroup(selectedReassignmentRequest.studentGroupId));
      queryClient.invalidateQueries(getStudentGroups());
      queryClient.invalidateQueries(getPendingStudentGroupReassignmentRequests());
      queryClient.invalidateQueries(getStudentGroupReassignmentRequests(selectedReassignmentRequest.studentGroupId));

      reset();
      closeModal();
      onSuccess?.();
    } catch (error) {
      const airServerError = airServerErrorFrom(error);
      toast({
        title: 'Error',
        description: airServerError?.message ?? 'Failed to update reassignment request',
        variant: 'destructive',
      });
    }
  };

  return (
    <FormModal
      modalTitle="Edit reassignment request"
      formSchema={reassignmentRequestFormSchema}
      defaultValues={defaultValues}
      resetOnDefaultValuesChange
      trigger={
        trigger ?? (
          <DropdownMenuItem disabled={!isAvailable} onSelect={(e) => e.preventDefault()}>
            Edit reassignment request
          </DropdownMenuItem>
        )
      }
      onSubmit={onSubmit}
      confirmBtnLabel="Save"
      dialogContentProps={{ className: 'max-w-lg lg:max-w-lg max-h-none gap-3 p-4' }}
      submitButtonProps={{
        disabled: !isAvailable,
      }}
    >
      {({ form }) => <ReassignmentRequestForm form={form} countAsReassignedLabel={countAsReassignedLabel} />}
    </FormModal>
  );
};
